import { questionTypes } from "../../config";
import { StringObj } from "../../type";

export const flgLabels: StringObj = {
  invalid: "不正値",
  blank: "空白",
  mergedCells: "結合セル",
};

export function removeBracket(str: string) {
  // 文字列から[]{}()（）を削除する
  return str.replace(/[\[\]{}()（）]/g, "");
}
function isIntegerString(value: string): boolean {
  // 正規表現で整数かどうかチェック（負の数も含む）
  if (!/^[-+]?\d+$/.test(value)) {
    return false;
  }

  // 数値に変換してから整数かどうか確認
  const num = Number(value);
  return Number.isInteger(num);
}

export async function checkExcelFormat(excelValues: (string | number)[][]) {
  let results = [];

  // todo: ロジック追加
  // G列に取り消し線が存在する
  // G列に太字が存在する

  // 結合されたセルが存在する	→	セルの結合を解除してください。
  await Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getActiveWorksheet();
    const usedRange = sheet.getUsedRange();
    const mergedAreas = usedRange.getMergedAreasOrNullObject();
    mergedAreas.load(["address", "cellCount"]);
    await context.sync();

    if (mergedAreas.address) {
      results.push({
        reviewPoint: "mergedCells",
        target: mergedAreas.address,
        problem: "結合されたセルが存在します",
        proposedRevision: `${mergedAreas.address}のセルの結合を解除してください`,
        must: true,
      });
    }
  });

  excelValues.forEach((row, index) => {
    if (index <= 5) {
      return;
    }
    // G列が空白かつ、F列が”小見出し”ではない行が存在する
    if (!row[5] && row[4] && row[4] != "小見出し") {
      results.push({
        reviewPoint: "blank",
        target: `${index + 1}行目`,
        problem: "G列が空白かつ、F列が”小見出し”ではない行が存在します",
        proposedRevision: `${index + 1}行目のG列に値を入力するか、小見出しを設定してください`,
        must: true,
      });
    }

    // F列に数値、もしくは”小見出し”以外の値が入っている
    if (row[4] && row[4] != "小見出し" && typeof row[4] != "number") {
      if (!isIntegerString(removeBracket(row[4].toString()))) {
        results.push({
          reviewPoint: "invalid",
          target: `${index + 1}行目`,
          problem: "F列に数値、もしくは”小見出し”以外の値が入っています",
          proposedRevision: `${index + 1}行目のF列には、数値か小見出しを設定してください`,
          must: true,
        });
      }
    }

    const rowTypes = ["合紙", "Q番号無し設問文", "設問補足文", "ページの始まり", "ここから本調査"];

    if (index > 0) {
      // D列の回答形式に「合紙」「Q番号無し設問文」「設問補足文」「ページの始まり」「ここから本調査」以外の項目が、2行以上連続で入力されている
      if (
        row[2] &&
        excelValues[index - 1][2] &&
        !rowTypes.includes(row[2].toString()) &&
        !rowTypes.includes(excelValues[index - 1][2].toString())
      ) {
        results.push({
          reviewPoint: "invalid",
          target: `${index + 1}行目`,
          problem:
            "D列の回答形式に「合紙」「Q番号無し設問文」「設問補足文」「ページの始まり」「ここから本調査」以外の項目が、2行以上連続で入力されています",
          proposedRevision: `${index + 1}行目のD列を確認してください`,
          must: true,
        });
      }

      // D列「Q番号無し設問文」の一つ上の行が「合紙」「ページの始まり」「ここから本調査」、もしくは空白　（何かしらの設問かQ番号無し設問文、設問補足文以外の項目になっている）
      // todo: 修正
      if (row[1].toString() == "Q番号無し設問文") {
        const prevValue = excelValues[index - 1][2].toString();
        if (["合紙", "ページの始まり", "ここから本調査"].includes(prevValue) || prevValue == "") {
          results.push({
            reviewPoint: "invalid",
            target: `${index + 1}行目`,
            problem:
              "D列「Q番号無し設問文」の一つ上の行が「合紙」「ページの始まり」「ここから本調査」、もしくは空白です",
            proposedRevision: `${index + 1}行目のD列を確認してください`,
            must: true,
          });
        }
      }

      // D列「Q番号無し設問文」の一つ上の行が「設問補足文」
      if (
        row[1].toString() == "Q番号無し設問文" &&
        excelValues[index - 1][2].toString() == "設問補足文"
      ) {
        results.push({
          reviewPoint: "invalid",
          target: `${index + 1}行目`,
          problem:
            "D列「Q番号無し設問文」の一つ上の行が「設問補足文」です。Q番号無し設問文にはには、補足文を設定することができません。",
          proposedRevision: `${index + 1}行目のD列を確認してください`,
          must: true,
        });
      }
    }
  });

  return results;
}

export function checkExcelFormatByQuestion(questionId: string, targetRows: StringObj[]) {
  let results = [];

  const titleRow = targetRows.filter(
    (row) =>
      (questionTypes.includes(row["questionType"]) && !row["rowType"]) ||
      row["rowType"] == "設問本文"
  )[0];

  targetRows.forEach((row, index) => {
    if (
      row["questionType"] == titleRow["questionType"] &&
      row["questionText"] == titleRow["questionText"]
    ) {
      return;
    }

    if (row["itemId"] == "小見出し") {
      // 「小見出し」行の下に選択肢が1つも無い
      const nextRow = targetRows[index + 1];
      if (!nextRow) {
        results.push({
          reviewPoint: "blank",
          target: `設問 ${questionId}`,
          problem: "「小見出し」行の下に選択肢が1つもありません",
          proposedRevision: `${index + 1}行目の下に選択肢を設定してください`,
          must: true,
          questionId: questionId,
        });
      }

      if (!nextRow["itemId"] || !nextRow["text"]) {
        results.push({
          reviewPoint: "blank",
          target: `設問 ${questionId}`,
          problem: "「小見出し」行の下に選択肢が1つもありません",
          proposedRevision: `${index + 1}行目の下に選択肢を設定してください`,
          must: true,
          questionId: questionId,
        });
      }
      // 「小見出し」行に表示条件が記載されている
      if (row["condition"]) {
        results.push({
          reviewPoint: "invalid",
          target: `設問 ${questionId}`,
          problem: "「小見出し」行に表示条件が記載されています",
          proposedRevision: `${index + 1}行目の表示条件を削除してください`,
          must: false,
          questionId: questionId,
        });
      }
    }

    if (!titleRow["questionType"].includes("マトリクス")) {
      // D列が”マトリクス”でないかつ、E列に表側の指定がある	→	表側はマトリクス以外の項目で設定しないでください。
      targetRows
        .filter((_, i) => i != index)
        .forEach((r) => {
          if (r["rowType"].includes("表側")) {
            results.push({
              reviewPoint: "invalid",
              target: `設問 ${questionId}`,
              problem: "マトリクス以外の設問で、表側が設定されています",
              proposedRevision: `${index + 1}行目の表側を削除してください`,
              questionId: questionId,
            });
          }
        });
    } else {
      // D列が”マトリクス”かつ、次の「ページの始まり」までに、E列に表側の指定がある行が存在しない、
      // もしくは、G列に値が入っているかつE列が空白の行が存在しない
      // →	マトリクスは表側と選択肢の両方を設定してください。
      if (!targetRows.filter((r) => r["rowType"].includes("表側")).length) {
        results.push({
          reviewPoint: "blank",
          target: `設問 ${questionId}`,
          problem: "マトリクスは表側を設定してください",
          proposedRevision: `${index + 1}行目の表側を設定してください`,
          questionId: questionId,
        });
      }
    }
  });

  return results.reduce((acc, cur) => {
    const existing = acc.find(
      (item) =>
        item.message === cur.message &&
        item.detail === cur.detail &&
        item.target === cur.target &&
        item.checkType === cur.checkType
    );
    if (!existing) {
      return [...acc, cur];
    }
    return acc;
  }, []);
}
